import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import '../assets/LegalPage.css';
import './FAQs.css';

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      category: 'Orders & Payment',
      questions: [
        {
          q: 'How do I place an order?',
          a: 'Browse our products, add items to your bag, and proceed to checkout. You can checkout as a guest or create an account for faster checkout and order tracking.',
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept Visa, Mastercard, American Express, Discover, PayPal, and other payment methods displayed at checkout. All transactions are secure.',
        },
        {
          q: 'When will I be charged?',
          a: 'Your payment method is charged when your order ships. For pre-orders or custom items, you may be charged at the time of purchase.',
        },
        {
          q: 'How can I track my order?',
          a: 'Once your order ships, you will receive an email with a tracking number. You can also track your order by logging into your account and visiting Order History.',
        },
      ],
    },
    {
      category: 'Shipping',
      questions: [
        {
          q: 'How much does shipping cost?',
          a: 'Standard shipping is free on orders of $49 or more. Orders under $49 have a flat rate of $9.99. Express and next-day shipping options are available at checkout.',
        },
        {
          q: 'How long does shipping take?',
          a: 'Standard shipping typically takes 5–7 business days. Express shipping takes 2–3 business days. Next-day shipping is available for most items when ordered before 2:00 PM ET.',
        },
        {
          q: 'Do you ship internationally?',
          a: 'We currently ship only within the United States and U.S. territories (Alaska, Hawaii, Puerto Rico, etc.). International shipping is not available at this time.',
        },
        {
          q: 'Can I change my shipping address after placing an order?',
          a: 'Contact us immediately if your order has not yet shipped. Once an order has shipped, we cannot change the address. You may need to contact the carrier for delivery options.',
        },
      ],
    },
    {
      category: 'Returns & Refunds',
      questions: [
        {
          q: 'What is your return policy?',
          a: 'Most items can be returned within 30 days of delivery for a full refund or exchange. Items must be unused, unworn, and in original condition with tags attached. See our Returns Policy for full details.',
        },
        {
          q: 'How do I start a return?',
          a: 'Log into your account, go to Order History, and select "Return Item" for the order. You will receive a prepaid return label by email. Alternatively, contact Customer Service.',
        },
        {
          q: 'When will I receive my refund?',
          a: 'Refunds are processed within 5–10 business days after we receive your return. The refund will be credited to your original payment method. It may take an additional 3–5 business days for your bank to process it.',
        },
        {
          q: 'Are there items that cannot be returned?',
          a: 'Yes. Final sale items, personal care products, and certain other items cannot be returned. These are clearly marked at the time of purchase.',
        },
      ],
    },
    {
      category: 'Account & Security',
      questions: [
        {
          q: 'How do I create an account?',
          a: 'Click "Sign In" in the header and select "Create Account." Enter your email and create a password. You can also create an account during checkout.',
        },
        {
          q: 'I forgot my password. How do I reset it?',
          a: 'Click "Sign In," then "Forgot Password." Enter your email address and we will send you a link to reset your password.',
        },
        {
          q: 'Is my personal information secure?',
          a: 'Yes. We use industry-standard encryption and security measures to protect your data. We do not store full credit card numbers. See our Privacy Policy for details.',
        },
      ],
    },
    {
      category: 'Products & Promotions',
      questions: [
        {
          q: 'How do I use a promo code?',
          a: 'Enter your promo code in the "Promo Code" field at checkout and click "Apply." The discount will be applied to your order if the code is valid.',
        },
        {
          q: 'Do you offer price adjustments?',
          a: 'We may offer a price adjustment if an item goes on sale within 14 days of your purchase. Contact Customer Service with your order number to inquire.',
        },
        {
          q: 'Are products authentic?',
          a: 'Yes. We only sell authentic products from authorized brands and suppliers. We stand behind the quality of everything we sell.',
        },
      ],
    },
  ];

  const toggleQuestion = (categoryIdx, questionIdx) => {
    const idx = `${categoryIdx}-${questionIdx}`;
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <div className="legal-page faqs-page">
      <div className="legal-container">
        <header className="legal-header">
          <h1>Frequently Asked Questions</h1>
          <p className="legal-meta">
            Find answers to common questions about orders, shipping, returns, and more.
          </p>
        </header>

        <div className="faqs-content">
          {faqs.map((category, catIdx) => (
            <section key={category.category} className="faqs-category">
              <h2>{category.category}</h2>
              <div className="faqs-list">
                {category.questions.map((item, qIdx) => {
                  const idx = `${catIdx}-${qIdx}`;
                  const isOpen = openIndex === idx;
                  return (
                    <div
                      key={qIdx}
                      className={`faqs-item ${isOpen ? 'open' : ''}`}
                    >
                      <button
                        type="button"
                        className="faqs-question"
                        onClick={() => toggleQuestion(catIdx, qIdx)}
                        aria-expanded={isOpen}
                      >
                        {item.q}
                        <ChevronDown size={20} className="faqs-chevron" />
                      </button>
                      <div className="faqs-answer">
                        <p>{item.a}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="faqs-contact-cta">
          <p>Still have questions? <Link to="/contact">Contact our Customer Service team</Link>.</p>
        </div>

        <div className="legal-footer-links">
          <Link to="/">Return to Home</Link>
          <Link to="/contact">Contact Us</Link>
          <Link to="/shipping">Shipping Policy</Link>
          <Link to="/returns">Returns Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default FAQs;
